import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';

// Fallback data shown while loading or if no commodities are configured
const FALLBACK_COMMODITIES = [
  { name: 'Soybeans', price: 14.52, change_pct: 1.2, trend: 'up' },
  { name: 'Coffee Arabica', price: 2.34, change_pct: -0.8, trend: 'down' },
  { name: 'Sugar #11', price: 0.27, change_pct: 0.3, trend: 'up' },
  { name: 'Iron Ore', price: 108.50, change_pct: 0.0, trend: 'flat' },
  { name: 'Corn', price: 6.78, change_pct: 0.5, trend: 'up' },
  { name: 'Cotton', price: 0.82, change_pct: -1.1, trend: 'down' },
];

const TREND_CONFIG = {
  up: { icon: TrendingUp, color: '#16A34A', bg: '#F0FDF4' },
  down: { icon: TrendingDown, color: '#DC2626', bg: '#FEF2F2' },
  flat: { icon: Minus, color: '#6B7280', bg: '#F9FAFB' },
};

const CommodityTracker = ({ count = 6 }) => {
  const { userTheme } = useTheme();
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await api.get('/commodities/prices');
        if (res.data && res.data.length > 0) {
          setCommodities(res.data);
          setIsLive(true);
        } else {
          setCommodities(FALLBACK_COMMODITIES);
        }
      } catch {
        setCommodities(FALLBACK_COMMODITIES);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, []);

  const items = commodities.slice(0, count);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100" style={{ backgroundColor: userTheme?.dashboardWidgetHeader?.backgroundColor }}>
        <BarChart3 className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold text-gray-800">Live Commodity Tracker</h3>
        <span className="ml-auto text-xs text-gray-400">
          {loading ? <RefreshCw className="w-3 h-3 animate-spin inline" /> : isLive ? 'Live' : 'Sample data'}
        </span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-100">
        {items.map((item, idx) => {
          const trend = TREND_CONFIG[item.trend] || TREND_CONFIG.flat;
          const TrendIcon = trend.icon;
          const price = typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : item.price;
          const changePct = item.change_pct != null ? item.change_pct : 0;
          const changeStr = `${changePct > 0 ? '+' : ''}${changePct.toFixed(1)}%`;

          return (
            <div key={idx} className="bg-white p-4">
              <p className="text-xs font-medium text-gray-500 mb-1">{item.name}</p>
              <p className="text-lg font-bold text-gray-900">{price}</p>
              <div className="flex items-center gap-1 mt-1">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ backgroundColor: trend.bg }}
                >
                  <TrendIcon className="w-3 h-3" style={{ color: trend.color }} />
                </div>
                <span className="text-xs font-medium" style={{ color: trend.color }}>
                  {changeStr}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommodityTracker;
